import React, { useState } from 'react';
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from 'react-redux';
import {
  Box, Stack, TextField, ToggleButtonGroup, ToggleButton,
  Card, CardContent, Typography, Chip, useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DataGrid } from '@mui/x-data-grid';
import Avatar from '@mui/material/Avatar';
import GpsNotFixedIcon from '@mui/icons-material/GpsNotFixed';
import IconButton from '@mui/material/IconButton';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import { fpzActions } from '../../actions';

export default function LibraryView({ rows }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

  const [q, setQ] = React.useState('');
  const [type, setType] = React.useState('all'); // all|folders|reports|unfiled
  const [activeFolderId, setActiveFolderId] = React.useState(null);
  const [expandedFolderIds, setExpandedFolderIds] = React.useState(() => new Set());
  const [imgError, setImgError] = useState(false);

  const toggleFolder = React.useCallback((folderId) => {
    setExpandedFolderIds((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) next.delete(folderId);
      else next.add(folderId);
      return next;
    });
  }, []);

  const handleViewReport = async (fpzReportId) => {
    const fpzReport = await fpzActions.getFpzReport(fpzReportId);
    if (!fpzReport) {
      console.error('No report details found');
      return;
    }
    await dispatch({
      type: 'SET_CURRENT_FPZ_REPORT', payload: {
        ...fpzReport?.report_details,
        fpz_report_id: fpzReport?.fpz_report_id || null,
        report_name: fpzReport?.report_name || '',
        report_description: fpzReport?.report_description || ''
      }
    });
    navigate(`/app/report`);
  };

  const visibleRows = React.useMemo(() => {
    return rows.filter((r) => {
      // Always show folder rows
      if (r.type === 'folder') return true;

      // Always show unfiled reports (or change to false if you want them hidden too)
      if (r.type === 'unfiled') return true;

      // Show report rows only when their folder is expanded
      if (r.type === 'report') return expandedFolderIds.has(r.folderId);

      return true;
    });
  }, [rows, expandedFolderIds]);

  const filtered = React.useMemo(() => {
    const query = q.trim().toLowerCase();

    return rows.filter((r) => {
      const matchesQ =
        !query ||
        r.name?.toLowerCase().includes(query) ||
        r.folderPath?.toLowerCase().includes(query);

      const matchesType =
        type === 'all' ||
        (type === 'folders' && r.type === 'folder') ||
        (type === 'reports' && r.type === 'report') ||
        (type === 'unfiled' && r.type === 'unfiled');

      const matchesFolder =
        !activeFolderId ||
        r.folderId === activeFolderId ||          // items in folder
        (r.type === 'folder' && r.folderId === activeFolderId); // keep folder row visible

      return matchesQ && matchesType && matchesFolder;
    });
  }, [rows, q, type, activeFolderId]);

  {/*} const columns = React.useMemo(() => ([
    {
      field: 'type',
      headerName: '',
      width: 90,
      sortable: false,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.value}
          variant="outlined"
        />
      ),
    },
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 180 },
    { field: 'folderPath', headerName: 'Folder', flex: 1, minWidth: 200 },
    { field: 'updatedAt', headerName: 'Updated', width: 160 },
  ]), []);*/
  }

  const columns = React.useMemo(() => ([
    {
      field: 'image',
      headerName: '',
      width: 76,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const row = params.row;

        if (row.type === 'folder') {
          const isOpen = expandedFolderIds.has(row.folderId);
          return (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                toggleFolder(row.folderId);
              }}
            >
              {isOpen ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
            </IconButton>
          );
        }

        return (
          <Avatar
            src={!imgError ? row?.image : undefined}
            onError={() => setImgError(true)}
            sx={{ width: 40, height: 40 }}
            variant="rounded"
          >
            <GpsNotFixedIcon />
          </Avatar>
        );
      },
    },
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 180 },
    { field: 'folderPath', headerName: 'Folder', flex: 1, minWidth: 200 },
    { field: 'updatedAt', headerName: 'Updated', width: 160 },
  ]), [expandedFolderIds, toggleFolder]);

  return (
    <Stack spacing={2}>
      {/* Filters (sticky on mobile is optional; keep it outside the table) */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ sm: 'center' }}
      >
        <TextField
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search…"
          size="small"
          fullWidth
        />

        <ToggleButtonGroup
          value={type}
          exclusive
          onChange={(_, v) => v && setType(v)}
          size="small"
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="folders">Folders</ToggleButton>
          <ToggleButton value="reports">Reports</ToggleButton>
          {/*<ToggleButton value="unfiled">Unfiled</ToggleButton>*/}
        </ToggleButtonGroup>
      </Stack>

      {/* Mobile: cards */}
      {!isMdUp && (
        <Stack spacing={1}>
          {filtered.map((r) => (
            <Card key={r.id} variant="outlined">
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography fontWeight={600} noWrap sx={{ maxWidth: '70%' }}>
                    {r.name}
                  </Typography>
                  <Chip size="small" label={r.type} variant="outlined" />
                </Stack>
                {r.folderPath && (
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {r.folderPath}
                  </Typography>
                )}
                {r.updatedAt && (
                  <Typography variant="caption" color="text.secondary">
                    Updated: {r.updatedAt}
                  </Typography>
                )}
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      {/* Desktop: table */}
      {isMdUp && (
        <Box sx={{ height: 640, width: '100%' }}>
          <DataGrid
            rows={filtered}
            columns={columns}
            getRowId={(r) => r.id}
            disableRowSelectionOnClick
            density="compact"
            pageSizeOptions={[25, 50, 100]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25, page: 0 } },
            }}
            onRowClick={(params) => {
              //if (params.row.type === 'folder') setActiveFolderId(params.row.folderId);
              //if (params.row.type === 'folder') toggleFolder(params.row.folderId);
              if (params.row.type !== 'folder') handleViewReport(params.row?.reportId);
            }}
          />
        </Box>
      )}
    </Stack>
  );
}
